import { Component, DoCheck, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  convertMinutesToUnit,
  convertToMinutes,
  TimeDelay,
  TimeUnit,
  TimeUnitDisplay,
  translateTimeUnitObject,
} from '../../../../utils/time-utils';
import { MatLegacyFormFieldAppearance as MatFormFieldAppearance } from '@angular/material/legacy-form-field';

@Component({
  selector: 'form-time-delay-input',
  templateUrl: './form-time-delay-input.component.html',
  styleUrls: ['./form-time-delay-input.component.scss'],
})
export class FormTimeDelayInputComponent implements OnInit, DoCheck {
  // Inputs provenant du parent
  @Input() formGroup: UntypedFormGroup;
  @Input() controlName: string;
  @Input() label: string;
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() icon: any;
  @Input() defaultErrorMessage: any;
  @Input() invalidPatternMessage: string;
  @Input() customErrorMessage: string;
  @Input() withoutPadding = false;
  @Input() minNumber: number;
  @Input() maxNumber: number;
  @Input() defaultTimeUnit: TimeUnitDisplay = TimeUnitDisplay.HOUR;
  @Input() defaultValue: TimeDelay; // Valeur par défaut fournie depuis le parent
  @Output() onLostFocus = new EventEmitter<void>();

  visible = false;
  timeDelayForm: UntypedFormGroup;
  timeUnits = Object.values(TimeUnitDisplay).map(timeUnit => translateTimeUnitObject(timeUnit));

  ngOnInit() {
    this.timeDelayForm = new UntypedFormGroup({
      quantity: new UntypedFormControl(null, Validators.required),
      displayedUnit: new UntypedFormControl(this.defaultTimeUnit, Validators.required),
    });

    if (this.defaultValue) {
      const displayedQuantity = convertMinutesToUnit(this.defaultValue.quantity, this.defaultValue.displayedUnit);
      this.timeDelayForm.setValue(
        {
          quantity: displayedQuantity,
          displayedUnit: this.defaultValue.displayedUnit,
        },
        { emitEvent: false },
      );
      this.formGroup.get(this.controlName)?.setValue(this.defaultValue);
    } else {
      this.timeDelayForm.setValue(
        {
          quantity: 0,
          displayedUnit: this.defaultTimeUnit,
        },
        { emitEvent: false },
      );
    }

    this.syncDisabledStateFromParent();

    this.timeDelayForm.valueChanges.subscribe(val => {
      if (val.quantity != null && val.displayedUnit != null) {
        const minutes = convertToMinutes(val.quantity, val.displayedUnit);
        const timeDelay: TimeDelay = {
          quantity: minutes,
          displayedUnit: val.displayedUnit,
        };
        this.formGroup.get(this.controlName)?.setValue(timeDelay);
      }
    });
  }

  ngDoCheck() {
    this.syncDisabledStateFromParent();
  }

  /**
   * Le formulaire interne (quantité + unité) n'est pas relié au contrôle du parent par Angular : un
   * `disable()` sur le formulaire parent (lecture seule, PRED-3230) doit être répercuté ici, sinon le
   * champ reste saisissable. `disable()` sans `emitEvent` n'émet pas de `statusChanges`, d'où la
   * synchronisation à chaque cycle de détection (comparaison triviale).
   */
  private syncDisabledStateFromParent() {
    const parentDisabled = !!this.formGroup?.get(this.controlName)?.disabled;
    if (!this.timeDelayForm || parentDisabled === this.timeDelayForm.disabled) {
      return;
    }
    if (parentDisabled) {
      this.timeDelayForm.disable({ emitEvent: false });
    } else {
      this.timeDelayForm.enable({ emitEvent: false });
    }
  }
}
